<script lang='ts'>
    import HelpIcon from './HelpIcon.svelte';

    let {
        value = $bindable(),
        disabled = false,
        class: xclass = '',
        onchange = null,
        onkeypress = null,
        help = undefined
    }: {
        value?: string;
        disabled?: boolean;
        class?: string;
        onchange?: ((value: string) => void) | null;
        onkeypress?: ((event: KeyboardEvent) => void) | null;
        help?: string;
    } = $props()
    let internalValue = $derived(value)

    function normalizePhoneNumber(phoneNumber: string | undefined) {
        // Remove all non-digit characters. Guard against non-string input so a
        // stray value can never throw inside this derived and abort the render.
        if (!phoneNumber || typeof phoneNumber !== 'string') return ''
        return phoneNumber.replace(/[()-]/g, '')
    }

    function formatPhoneNumber(phoneNumber: string | undefined) {
        if (!phoneNumber || typeof phoneNumber !== 'string') return ''
        const cleaned = normalizePhoneNumber(phoneNumber)
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`
        }
        return phoneNumber
    }

    function handleInput(event: Event) {
        let input = (event.target as HTMLInputElement).value.replace(/\D/g, '')
        if (input.length === 11 && input.startsWith('1')) {
            input = input.slice(1)
        }
        if (input.length <= 10) {
            internalValue = input
        }
    }

    function handleBlur() {
        value = internalValue
        onchange?.(internalValue ?? '')
    }

    let formattedValue = $derived(formatPhoneNumber(value))

    // Validate by digit count, not raw string length: a stored value may be
    // formatted (e.g. "(903) 466-6114"), which is never literally 10 chars, so
    // the old check flagged valid numbers as invalid until they were re-entered.
    let isValid = $derived(!value || (typeof value === 'string' && value.replace(/\D/g, '').length === 10))

    $effect(() => {
        internalValue = value
    })
</script>

{#if help}
    <div class="input-with-help">
        <input
            type="tel"
            autocomplete="tel"
            value={formattedValue}
            oninput={handleInput}
            onblur={handleBlur}
            onkeypress={onkeypress}
            {disabled}
            class="input-phone m-0 p-0 pl-4 h-8 border-[1px] border-solid border-primary rounded focus:ring-primary
                disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none
                {!isValid && !disabled ? 'invalid' : ''} {xclass}"
        />
        <HelpIcon {help}/>
    </div>
{:else}
    <input
        type="tel"
        autocomplete="tel"
        value={formattedValue}
        oninput={handleInput}
        onblur={handleBlur}
        onkeypress={onkeypress}
        {disabled}
        class="m-0 p-0 pl-4 h-8 border-[1px] border-solid border-primary rounded focus:ring-primary
            disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none
            {!isValid && !disabled ? 'invalid' : ''} {xclass}"
    />
{/if}

<style>
    .input-with-help {
        display: inline-flex;
        align-items: center;
        width: 100%;
    }

    .input-with-help .input-phone {
        flex: 1;
    }

    .disabled {
        background-color: #e0e0e0;
        cursor: not-allowed;
    }

    .invalid {
        background-color: #ffcccc;
    }
</style>
